import { useState, useCallback, useEffect } from 'react';
import { message } from 'antd';
import { aiConfigService } from '../../../services/aiConfigService';
import type { Dataset } from '../../../services/aiConfigService';
import { buildDatasetTree } from '../utils/treeBuilder';
import type { TreeNode } from '../utils/treeBuilder';

export const useDatasetConfig = () => {
  const [datasets, setDatasets] = useState<Dataset[]>([]);
  const [treeData, setTreeData] = useState<TreeNode[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [editingRecord, setEditingRecord] = useState<Dataset | null>(null);
  const [formRef, setFormRef] = useState<any>(null);
  const [expandedKeys, setExpandedKeys] = useState<React.Key[]>([]);
  const [selectedKeys, setSelectedKeys] = useState<React.Key[]>([]);
  const [selectedNode, setSelectedNode] = useState<any>(null);

  const loadDatasets = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await aiConfigService.getDatasetList();
      setDatasets(data);
      setTreeData(buildDatasetTree(data));
    } catch (err: any) {
      setError(err?.message || '加载数据集失败');
      if (err?.response?.status !== 404 && !err?.message?.includes('404')) {
        message.error(err?.message || '加载数据集失败');
      } else {
        setDatasets([]);
        setTreeData([]);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const loadTreeData = useCallback(async () => {
    try {
      const data = await aiConfigService.getDatasetList();
      setTreeData(buildDatasetTree(data));
    } catch (err: any) {
      message.error(`加载树形数据失败: ${err?.message}`);
    }
  }, []);

  const handleSelect = useCallback((keys: React.Key[]) => {
    if (keys.length === 0) {
      setSelectedNode(null);
      setSelectedKeys([]);
      return;
    }
    setSelectedKeys(keys);
  }, []);

  const handleOpenModal = useCallback((record?: Dataset | null) => {
    setEditingRecord(record ?? null);
    setModalVisible(true);
  }, []);

  const handleCloseModal = useCallback(() => {
    setModalVisible(false);
    setEditingRecord(null);
    formRef?.resetFields?.();
  }, [formRef]);

  const handleSave = useCallback(async () => {
    if (!formRef) return;
    try {
      const values = await formRef.validateFields();
      
      // 处理思考方式关联数据（只保留思考方式ID数组）
      const thinkingModes = Array.isArray(values.thinkingModes)
        ? values.thinkingModes.map((thinkingModeId: string) => ({
            thinkingModeId,
          }))
        : [];

      const payload = {
        ...editingRecord,
        ...values,
        paramTemplate: values.paramTemplate
          ? typeof values.paramTemplate === 'string'
            ? JSON.parse(values.paramTemplate)
            : values.paramTemplate
          : undefined,
        defaultParams: values.defaultParams
          ? typeof values.defaultParams === 'string'
            ? JSON.parse(values.defaultParams)
            : values.defaultParams
          : undefined,
        thinkingModes,
      };
      
      // TODO: 使用 mock 数据，后续替换为真实 API
      console.log('保存数据集（Mock）:', payload);
      // await aiConfigService.upsertDataset(payload);
      message.success('保存成功（Mock 模式）');
      handleCloseModal();
      loadDatasets();
    } catch (e: any) {
      if (e?.errorFields) return;
      message.error(e?.message || '保存失败');
    }
  }, [formRef, editingRecord, handleCloseModal, loadDatasets]);

  const handleDelete = useCallback(
    async (datasetId: string) => {
      try {
        await aiConfigService.deleteDataset(datasetId);
        message.success('删除成功');
        loadDatasets();
      } catch (err: any) {
        message.error(err?.message || '删除失败');
      }
    },
    [loadDatasets]
  );

  useEffect(() => {
    loadDatasets();
  }, [loadDatasets]);

  return {
    datasets,
    treeData,
    loading,
    error,
    modalVisible,
    editingRecord,
    formRef,
    setFormRef,
    expandedKeys,
    selectedKeys,
    selectedNode,
    loadDatasets,
    loadTreeData,
    handleSelect,
    handleOpenModal,
    handleCloseModal,
    handleSave,
    handleDelete,
    setExpandedKeys,
  };
};
